import React, { useEffect } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { getRandomPhrase, type RoaryPhraseKey } from './roaryPhrases';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

/** Maps toast type to a Roary head image. Returns null for error toasts. */
const getRoaryToastImage = (type: ToastType): string | null => {
  switch (type) {
    case 'success':
      return '/roary/roary-celebrate.png';
    case 'info':
      return '/roary/roary-neutral.png';
    case 'warning':
      return '/roary/roary-worried.png';
    case 'error':
      return null;
  }
};

interface ToastProps {
  message: string;
  type?: ToastType;
  duration?: number;
  onClose: () => void;
  isVisible: boolean;
  title?: string;
  /** Show Roary head icon instead of the default Lucide icon. Ignored for 'error' type. */
  showRoary?: boolean;
}

const getToastStyles = (type: ToastType) => {
  switch (type) {
    case 'success':
      return {
        icon: CheckCircle,
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        iconColor: 'text-green-600',
        titleColor: 'text-green-900',
        textColor: 'text-green-800',
      };
    case 'error':
      return {
        icon: XCircle,
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        iconColor: 'text-red-600',
        titleColor: 'text-red-900',
        textColor: 'text-red-800',
      };
    case 'warning':
      return {
        icon: AlertTriangle,
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        iconColor: 'text-yellow-600',
        titleColor: 'text-yellow-900',
        textColor: 'text-yellow-800',
      };
    case 'info':
      return {
        icon: Info,
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        iconColor: 'text-blue-600',
        titleColor: 'text-blue-900',
        textColor: 'text-blue-800',
      };
  }
};

export const Toast: React.FC<ToastProps> = ({
  message,
  type = 'info',
  duration = 5000,
  onClose,
  isVisible,
  title,
  showRoary = false,
}) => {
  useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  if (!isVisible) return null;

  const styles = getToastStyles(type);
  const Icon = styles.icon;
  const roaryImage = showRoary ? getRoaryToastImage(type) : null;

  return (
    <div className="fixed top-4 right-4 z-[70] animate-slide-in-right">
      <div
        className={`${styles.bgColor} ${styles.borderColor} border rounded-lg shadow-lg p-4 max-w-md min-w-[300px]`}
      >
        <div className="flex items-center">
          <div className="flex-shrink-0">
            {roaryImage ? (
              <img src={roaryImage} alt="Roary" className="w-10 h-10 object-contain" />
            ) : (
              <Icon className={`w-5 h-5 ${styles.iconColor}`} />
            )}
          </div>
          <div className="ml-3 flex-1">
            {title && <p className={`text-sm font-medium ${styles.titleColor}`}>{title}</p>}
            <p className={`text-sm ${styles.textColor} ${title ? 'mt-1' : ''}`}>{message}</p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 flex-shrink-0 rounded-md hover:bg-white/20 p-1 transition-colors"
          >
            <X className={`w-4 h-4 ${styles.iconColor}`} />
          </button>
        </div>
      </div>
    </div>
  );
};

// Toast Container to manage multiple toasts
export interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
  title?: string;
  showRoary?: boolean;
}

interface ToastContainerProps {
  toasts: ToastItem[];
  removeToast: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, removeToast }) => {
  return (
    <div className="fixed top-4 right-4 z-[70] space-y-2">
      {toasts.map((toast, index) => (
        <div
          key={toast.id}
          className="animate-slide-in-right"
          style={{ animationDelay: `${index * 100}ms` }}
        >
          <Toast
            message={toast.message}
            type={toast.type}
            title={toast.title}
            showRoary={toast.showRoary}
            isVisible={true}
            onClose={() => removeToast(toast.id)}
          />
        </div>
      ))}
    </div>
  );
};

// Options for Roary-enhanced toasts
export interface ToastOptions {
  title?: string;
  showRoary?: boolean;
  /** When provided with showRoary, overrides title/message with a random Roary phrase. */
  phraseKey?: RoaryPhraseKey;
}

// Custom hook for managing toasts
// eslint-disable-next-line react-refresh/only-export-components
export const useToast = (canUseRoaryBranding = true) => {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);

  const showToast = (
    message: string,
    type: ToastType = 'info',
    options?: string | ToastOptions
  ) => {
    // Support legacy signature: showToast('msg', 'type', 'title')
    let opts: ToastOptions = typeof options === 'string' ? { title: options } : (options ?? {});

    if (opts.showRoary && !canUseRoaryBranding) {
      opts = { title: opts.title };
    }

    // Resolve phrase at creation time (no lifecycle concerns for toasts)
    let displayTitle = opts.title;
    let displayMessage = message;
    if (opts.showRoary && opts.phraseKey) {
      const phrase = getRandomPhrase(opts.phraseKey);
      displayTitle = phrase.title;
      displayMessage = phrase.message;
    }

    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [
      ...prev,
      {
        id,
        message: displayMessage,
        type,
        title: displayTitle,
        showRoary: opts.showRoary,
      },
    ]);

    // Auto remove after 5 seconds
    setTimeout(() => {
      removeToast(id);
    }, 5000);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  return { toasts, showToast, removeToast };
};
